import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { useWindowSize } from '@components/component-helpers';
import { EmptySearchState } from '@components/empty-search-state';
import { EmptyState } from '@components/empty-state';
import { IconExclamation } from '@components/icons/exclamation';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useIsMobile } from '@hooks/use-is-mobile';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { throttle } from '@utils/browser';
import { isConnectionError } from '@utils/requests';

import { useJobList } from 'api/jobs/hooks';
import { Loading } from 'components/Loading';
import { LoadingMore } from 'components/LoadingMore';
import { PageContent } from 'components/Page';
import { useStore } from 'models/Provider';
import type { JobListOrdering, JobListRow, JobState } from 'types/jobs';

import { JobListItem } from './job-list-item';
import { JOB_TAB_ICONS } from './job-tab-icons';
import { jobsLocale, snapToColumnBreakpoint } from './jobs-config';
import { MobileJobListItem } from './mobile-job-list-item';
import { useJobActions } from './use-job-actions';

// Long enough that a cached response doesn't flash a spinner.
const SPINNER_DELAY_MS = 300;
// Includes the 80px `hasScrollEndSpace` gutter.
const SCROLL_FETCH_THRESHOLD_PX = 160;
const SCROLL_THROTTLE_MS = 200;

type JobsProps = {
  status: JobState;
  searchStr: string;
  ordering: JobListOrdering;
  onClearSearch: () => void;
};

export const Jobs = ({ status, searchStr, ordering, onClearSearch }: JobsProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const {
    data,
    error,
    isLoading,
    isError,
    isFetching,
    isPreviousData,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useJobList({ status, ordering, search: searchStr });

  // keepPreviousData holds the outgoing tab's rows, so drop them until the new key resolves.
  const jobs = useMemo(
    () => (isPreviousData ? [] : (data?.pages.flatMap((page) => page.results) ?? [])),
    [data, isPreviousData],
  );

  // One error flag covers a failed next page and a failed refetch; retry repeats whichever.
  const nextPageFailed = useRef(false);

  // One persistent scroller: without this a list change leaves you mid-list and fetches its next page.
  useLayoutEffect(() => {
    scrollRef.current?.scrollTo(0, 0);
    nextPageFailed.current = false;
  }, [status, ordering, searchStr]);

  useEffect(() => {
    if (isFetching && !isFetchingNextPage) nextPageFailed.current = false;
  }, [isFetching, isFetchingNextPage]);

  // An errored query keeps `status: 'error'` while refetching, so `isLoading` stays false.
  const isRetrying = isError && isFetching;
  const isOffline = isError && isConnectionError(error);

  const loadNextPage = useCallback(
    () =>
      fetchNextPage().then((result) => {
        nextPageFailed.current = result.isError;
      }),
    [fetchNextPage],
  );

  const onScroll = useMemo(
    () =>
      throttle((e: { target: HTMLElement }) => {
        if (!hasNextPage || isFetchingNextPage) return;
        const el = e.target;
        if (el.scrollHeight - (el.clientHeight + el.scrollTop) < SCROLL_FETCH_THRESHOLD_PX) {
          loadNextPage();
        }
      }, SCROLL_THROTTLE_MS),
    [hasNextPage, isFetchingNextPage, loadNextPage],
  );

  return (
    <PageContent
      ref={scrollRef}
      hasScrollEndSpace
      onScroll={(e) => {
        onScroll({ target: e.currentTarget });
      }}
    >
      {(isLoading || isPreviousData || isRetrying) && <Loading delay={SPINNER_DELAY_MS} />}
      <JobListPanel
        jobs={jobs}
        status={status}
        // A tab switch discards the outgoing rows, so treat it as loading —
        // otherwise the empty state flashes before the new page arrives.
        isLoading={isLoading || isPreviousData}
        isError={isError}
        isOffline={isOffline}
        isRetrying={isRetrying}
        onRetry={refetch}
        searchStr={searchStr}
        onClearSearch={onClearSearch}
      />
      {isFetchingNextPage && <LoadingMore isInline />}
      {/* A rejected page leaves the loaded rows in place, so the panel's error state
          never fires and the spinner just disappears — with no new content, no further
          scroll event arrives to retry either. */}
      {isError && jobs.length > 0 && !isFetchingNextPage && (
        <LoadMoreError
          isOffline={isOffline}
          isRetrying={isFetching}
          onRetry={() => (nextPageFailed.current ? loadNextPage() : refetch())}
        />
      )}
    </PageContent>
  );
};

type JobListPanelProps = {
  jobs: JobListRow[];
  status: JobState;
  isLoading: boolean;
  isError: boolean;
  isOffline: boolean;
  isRetrying: boolean;
  onRetry: () => void;
  searchStr: string;
  onClearSearch: () => void;
};

const JobListPanel = ({
  jobs,
  status,
  isLoading,
  isError,
  isOffline,
  isRetrying,
  onRetry,
  searchStr,
  onClearSearch,
}: JobListPanelProps) => {
  const { t } = useStore();
  const { t: tJobs } = useScopedTranslate(jobsLocale);
  const isMobile = useIsMobile();
  const TabIcon = JOB_TAB_ICONS[status];
  // Hoisted out of the rows — see JobListItemProps.
  const { width } = useWindowSize();
  const columnWidth = snapToColumnBreakpoint(width);
  const buildActionItems = useJobActions();

  // Before the empty check — otherwise a failed request reads as "there are no jobs".
  if (isError && jobs.length === 0) {
    return (
      <EmptyState
        skipMargin
        role="alert"
        header={tJobs('loadError')}
        content={tJobs(isOffline ? 'loadErrorOffline' : 'loadErrorSubtitle')}
        icon={<IconExclamation />}
        action={
          <Button
            variant="secondary"
            text={tJobs('loadErrorRetry')}
            onClick={onRetry}
            isDisabled={isRetrying}
          />
        }
      />
    );
  }

  if (jobs.length === 0 && !isLoading) {
    return searchStr ? (
      <EmptySearchState skipMargin ariaLive="polite" onClearSearch={onClearSearch} />
    ) : (
      <EmptyState
        skipMargin
        header={t(`jobs.nojobs.${status}`)}
        content={t(`jobs.alljobs.${status}`)}
        // A magnifying glass would read as an empty search result.
        icon={<TabIcon />}
      />
    );
  }

  return (
    <Stack space={2}>
      {jobs.map((job) =>
        isMobile ? (
          <MobileJobListItem key={job.id} job={job} />
        ) : (
          <JobListItem
            key={job.id}
            job={job}
            width={columnWidth}
            buildActionItems={buildActionItems}
          />
        ),
      )}
    </Stack>
  );
};

const LoadMoreError = ({
  isOffline,
  isRetrying,
  onRetry,
}: {
  isOffline: boolean;
  isRetrying: boolean;
  onRetry: () => void;
}) => {
  const { t: tJobs } = useScopedTranslate(jobsLocale);
  return (
    <LoadMoreErrorContainer
      space={2}
      orientation="horizontal"
      align="center"
      justify="center"
      role="alert"
    >
      <Text
        font="regular14"
        color="textSecondary"
        text={tJobs(isOffline ? 'loadErrorOffline' : 'loadError')}
      />
      <Button
        variant="secondary"
        text={tJobs('loadErrorRetry')}
        onClick={onRetry}
        isDisabled={isRetrying}
      />
    </LoadMoreErrorContainer>
  );
};

const LoadMoreErrorContainer = styled(Stack)({
  marginTop: theme.space[4],
});
